import { FC, Fragment, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Popover, Skeleton, Typography } from '@mui/material';
import type { FundingRoundItem } from 'api';
import { useGetCompanyFundingRounds } from 'services/hooks/companies';
import { SacraTimestamp } from '../../CompanyOverview.styles';
import {
  CITATION_TYPE_LABELS,
  formatAmount,
  formatDate,
  ROUND_TYPE_LABELS,
} from '../FundingRoundsCard.constants';
import {
  DocLink,
  ExpandButton,
  ExtensionBadge,
  InfoButton,
  MetaDivider,
  MetaLabel,
  MetaPopoverBox,
  MetaRow,
  MetaValue,
  RoundsTable,
  RoundsTableCell,
  RoundsTableHead,
  RoundsTableRow,
  ShareClassRow,
  TrancheRow,
} from '../FundingRoundsCard.styles';
import { SacraFundingRoundsCardProps } from './SacraFundingRoundsCard.props';

const COLUMN_HEADERS = ['Round', 'Date', 'Amount Raised', 'Valuation', 'Share Price', 'Status', 'Citations'];

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const MetaPopover: FC<{ round: FundingRoundItem; anchorEl: HTMLElement | null; onClose: () => void }> = ({
  round,
  anchorEl,
  onClose,
}) => {
  const open = Boolean(anchorEl);
  const eventRows: [string, string][] = [];
  if (round.event_type) eventRows.push(['Event type', round.event_type]);
  if (round.event_subtype) eventRows.push(['Event subtype', round.event_subtype]);
  if (round.event_source) eventRows.push(['Source', round.event_source]);
  if (round.event_last_updated_at) eventRows.push(['Last updated', formatDateTime(round.event_last_updated_at)]);
  const hasCitationDates = round.citations.some(c => c.updated_at);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <MetaPopoverBox>
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
          {round.round_name} — Metadata
        </Typography>
        {eventRows.map(([label, value]) => (
          <MetaRow key={label}>
            <MetaLabel>{label}</MetaLabel>
            <MetaValue>{value}</MetaValue>
          </MetaRow>
        ))}
        {hasCitationDates && (
          <>
            <MetaDivider />
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, fontSize: '0.68rem', color: 'text.secondary' }}>
              Citations
            </Typography>
            {round.citations.map((c, i) => (
              <MetaRow key={i}>
                <MetaLabel>{(c.type && CITATION_TYPE_LABELS[c.type]) ?? c.type ?? 'Document'}</MetaLabel>
                <MetaValue>{c.updated_at ? `Updated ${formatDateTime(c.updated_at)}` : '—'}</MetaValue>
              </MetaRow>
            ))}
          </>
        )}
        {eventRows.length === 0 && !hasCitationDates && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No additional metadata available.
          </Typography>
        )}
      </MetaPopoverBox>
    </Popover>
  );
};

const RoundRow: FC<{ round: FundingRoundItem; isExpanded: boolean; onToggle: () => void }> = ({
  round,
  isExpanded,
  onToggle,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasTranches = round.tranches.length > 1;
  const extraShareClasses = round.share_details.slice(1);
  const sharePrice = round.share_details[0]?.issue_price ?? '—';
  const roundLabel = round.round_type
    ? (ROUND_TYPE_LABELS[round.round_type] ?? round.round_name)
    : round.round_name;
  const hasMetadata =
    Boolean(round.event_type || round.event_subtype || round.event_source || round.event_last_updated_at) ||
    round.citations.some(c => c.updated_at);

  return (
    <>
      <RoundsTableRow>
        <RoundsTableCell>
          {hasTranches && (
            <ExpandButton onClick={onToggle} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
              {isExpanded ? '▾' : '▸'}
            </ExpandButton>
          )}
          {roundLabel}
          {round.is_extension && <ExtensionBadge>Ext.</ExtensionBadge>}
          {hasMetadata && (
            <InfoButton onClick={e => setAnchorEl(e.currentTarget)} aria-label="Show metadata">
              <InfoOutlinedIcon sx={{ fontSize: '0.85rem' }} />
            </InfoButton>
          )}
          <MetaPopover round={round} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
        </RoundsTableCell>
        <RoundsTableCell>{formatDate(round.announced_date)}</RoundsTableCell>
        <RoundsTableCell>{formatAmount(round.amount_raised, round.currency)}</RoundsTableCell>
        <RoundsTableCell>{formatAmount(round.valuation, round.currency)}</RoundsTableCell>
        <RoundsTableCell>{sharePrice}</RoundsTableCell>
        <RoundsTableCell sx={{ textTransform: 'capitalize' }}>{round.status ?? '—'}</RoundsTableCell>
        <RoundsTableCell>
          {round.citations.length > 0
            ? round.citations.map((c, i) => c.url ? (
              <DocLink key={i} href={c.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>
                {(c.type && CITATION_TYPE_LABELS[c.type]) ?? c.type ?? 'Link'}
              </DocLink>
            ) : null)
            : '—'}
        </RoundsTableCell>
      </RoundsTableRow>
      {extraShareClasses.map((sc, i) => (
        <ShareClassRow key={i}>
          <td style={{ paddingLeft: '24px' }}>{sc.name ?? '—'}</td>
          <td>—</td><td>—</td><td>—</td>
          <td>{sc.issue_price ?? '—'}</td>
          <td>—</td><td>—</td>
        </ShareClassRow>
      ))}
    </>
  );
};

const SacraFundingRoundsCard: FC<SacraFundingRoundsCardProps> = ({ companyId }) => {
  const { data, isLoading } = useGetCompanyFundingRounds(companyId);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const rounds = data?.rounds ?? [];

  const toggle = (key: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  if (isLoading) {
    return <Skeleton variant="rectangular" width="100%" height={160} sx={{ borderRadius: 1 }} />;
  }

  if (rounds.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
        No fundraising data available from Sacra for this company.
      </Typography>
    );
  }

  return (
    <>
      {data?.sacra_fetched_at && (
        <SacraTimestamp sx={{ display: 'block', mb: 1 }}>
          fetched {new Date(data.sacra_fetched_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </SacraTimestamp>
      )}
      <RoundsTable>
        <thead>
          <RoundsTableRow>
            {COLUMN_HEADERS.map(h => <RoundsTableHead key={h}>{h}</RoundsTableHead>)}
          </RoundsTableRow>
        </thead>
        <tbody>
          {rounds.map((round, i) => {
            const key = `${round.round_name}-${round.announced_date ?? i}`;
            const isExpanded = expanded.has(key);
            return (
              <Fragment key={key}>
                <RoundRow round={round} isExpanded={isExpanded} onToggle={() => toggle(key)} />
                {isExpanded && round.tranches.map((tranche, j) => (
                  <TrancheRow key={j}>
                    <td aria-label="indent" />
                    <td>{formatDate(tranche.announced_date)}</td>
                    <td>{formatAmount(tranche.amount_raised, tranche.currency)}</td>
                    <td>{formatAmount(tranche.valuation, tranche.currency)}</td>
                    <td>—</td>
                    <td style={{ textTransform: 'capitalize' }}>{tranche.status ?? '—'}</td>
                    <td>—</td>
                  </TrancheRow>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </RoundsTable>
    </>
  );
};

export default SacraFundingRoundsCard;
